// @ts-nocheck
/**
 * Reusable ConversationView Component
 * This is an exact replica of the messages window that can be used in modals or other contexts
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import SenderBadge, { senderOf } from './SenderBadge';
import { createPortal } from 'react-dom';
import { MessageCircle, Mail, Phone, User, Plus, ChevronDown, Building2, Calendar, CheckCircle, FileText, Eye, Download, Ban, Sparkles, Send } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import { Customer } from '../../lib/customers';
import { CommunicationCard, TimelineItem, Message, BookingMilestone } from '../../types/messages';
import { getCustomerMilestones } from '../../lib/bookingMilestones';
import { isCoreMessagesEnabled, fetchCoreThread, conversationKeyForCard, resolveCurrentSender, PHONE_READ_KINDS, type CoreReadKind } from '../../lib/messagesCore';
import { sendSMS } from '../../lib/messaging';
import { EmailReplyComposer } from '../email/EmailReplyComposer';
import { EmailDisplay } from '../email/EmailDisplay';
import { SMSReplyComposer } from '../SMSReplyComposer';
import SnippetsModal from '../SnippetsModal';
import toast from 'react-hot-toast';
import { replaceSnippetVariables as substituteSnippetVariables } from '../../lib/snippetVariables';
import MediaDisplayComponent from './MediaDisplayComponent';
import { useImageViewer } from '../ImageViewerProvider';
import { mediaUrlsToViewerImages } from '../../lib/messageMedia';
import { useNavigate } from 'react-router-dom';
import CreateModal from './CreateModal';
import { useModalZIndex } from '../../contexts/ModalStackContext';
import ConversationLeadBanner from './ConversationLeadBanner';
import CallRecordingDetails from './CallRecordingDetails';
import {
  getOpenOpportunitiesForCustomerIds,
  resolveCustomerIdsForContact,
  type OpenOpportunitySummary,
} from '../../lib/crm/opportunityLinking';
import {
  scheduleOpportunityFollowUp,
  markOpportunityFollowUpComplete,
} from '../../lib/crm/opportunityFollowUp';
import { moveOpportunityToStage } from '../../lib/crm/opportunityStage';
import CrmEmbeddedOpportunityModal from '../admin-dashboard/CrmEmbeddedOpportunityModal';
import { LEAD_CREATED_EVENT } from '../../lib/crm/createLead';
import { useKeyboardBottomInset } from '../../hooks/useKeyboardBottomInset';

// SMS composer drafts for the modal conversation view, mirrored to sessionStorage
// so a full reload (PWA auto-update, etc.) doesn't lose typed text. Uses a distinct
// key prefix from the Messages page so the two surfaces never clobber each other.
const MODAL_DRAFT_PREFIX = 'msg-modal-draft:';
function modalDraftKey(businessId: string | undefined | null, cardId: string): string {
  return `${MODAL_DRAFT_PREFIX}${businessId || 'nb'}:${cardId}`;
}
function readModalDraft(businessId: string | undefined | null, cardId: string): string {
  try {
    return sessionStorage.getItem(modalDraftKey(businessId, cardId)) || '';
  } catch {
    return '';
  }
}
function writeModalDraft(businessId: string | undefined | null, cardId: string, text: string): void {
  try {
    if (text) sessionStorage.setItem(modalDraftKey(businessId, cardId), text);
    else sessionStorage.removeItem(modalDraftKey(businessId, cardId));
  } catch {
    // sessionStorage unavailable / quota exceeded — non-critical
  }
}

// Helper functions
const normalizeEmailSubject = (subject: string) => {
  return decodeEmailSubject(subject)
    .replace(/^(re:|fwd:|fw:)\s*/gi, '')
    .trim()
    .toLowerCase();
};

const formatBytes = (bytes?: number | null) => {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
};

const decodeEmailSubject = (value: string) => {
  if (!value) return '';
  const decodeMimeWord = (charset: string, encoding: string, text: string) => {
    try {
      if (encoding.toLowerCase() === 'b') {
        const bytes = Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
        return new TextDecoder(charset || 'utf-8').decode(bytes);
      }
      const decoded = text
        .replace(/_/g, ' ')
        .replace(/=([0-9A-F]{2})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
      return decoded;
    } catch {
      return text;
    }
  };

  const decoded = value.replace(/=\?([^?]+)\?([bqBQ])\?([^?]*)\?=/g, (_, charset, encoding, text) => {
    return decodeMimeWord(charset, encoding, text);
  });

  const cleaned = decoded.replace(/[\u0000-\u001F\u007F]/g, '').trim();
  if (/[Ãâ¤�]/.test(cleaned)) {
    try {
      const bytes = Uint8Array.from(cleaned, (c) => c.charCodeAt(0));
      const utf8Decoded = new TextDecoder('utf-8').decode(bytes).replace(/[\u0000-\u001F\u007F]/g, '').trim();
      if (utf8Decoded) return utf8Decoded;
    } catch {
      // fall through
    }
    try {
      const legacyDecoded = decodeURIComponent(escape(cleaned));
      return legacyDecoded.replace(/[\u0000-\u001F\u007F]/g, '').replace(/[�]/g, '').trim();
    } catch {
      return cleaned.replace(/[�]/g, '').trim();
    }
  }
  return cleaned.replace(/[�]/g, '').trim();
};

interface EmailAttachment {
  id: string;
  gmail_id: string;
  filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  public_url: string;
  is_inline: boolean;
}

interface ConversationViewProps {
  card: CommunicationCard;
  markMessageAsRead: (messageId: string) => Promise<void>;
  /** Mark several messages read at once (one email thread). Falls back to markMessageAsRead per id. */
  markMessagesAsRead?: (messageIds: string[]) => Promise<void>;
  locallyReadMessages: Set<string>;
  fetchMilestones: (customerId: string, setLoading?: (loading: boolean) => void) => Promise<void>;
  onMobileBack?: () => void;
  onClose?: () => void;
  hideCloseButton?: boolean;
  customers?: Customer[];
  onEditCustomer?: (customer: Customer) => void;
  onCreateCustomer?: (card: CommunicationCard) => void;
  onMilestoneClick?: (milestone: BookingMilestone) => void;
  markAllMessagesAsRead?: (card: CommunicationCard, kinds?: CoreReadKind[]) => Promise<void>;
  extractEmailAddress?: (contactInfo: string) => string;
  initialReplyMethod?: 'sms' | 'email';
  emailAttachmentsByGmailId?: Record<string, EmailAttachment[]>;
  timelineAttachmentsByGmailId?: Record<string, EmailAttachment[]>;
  muteEmailSender?: (emailAddress: string) => Promise<void>;
  unmuteEmailSender?: (emailAddress: string) => Promise<void>;
  normalizeEmail?: (email: string) => string;
  onCreateAction?: (type: 'booking' | 'mobileBooking' | 'proposal' | 'placeOrder') => void;
}

export default function ConversationView({
  card,
  markMessageAsRead,
  markMessagesAsRead,
  locallyReadMessages,
  fetchMilestones,
  onMobileBack,
  onClose,
  hideCloseButton = false,
  customers = [],
  onEditCustomer,
  onCreateCustomer,
  onMilestoneClick,
  markAllMessagesAsRead,
  extractEmailAddress = (contactInfo: string) => {
    const emailMatch = contactInfo.match(/<([^>]+)>/);
    return emailMatch ? emailMatch[1] : contactInfo;
  },
  initialReplyMethod = 'sms',
  emailAttachmentsByGmailId = {},
  timelineAttachmentsByGmailId = {},
  muteEmailSender,
  unmuteEmailSender,
  normalizeEmail = (email: string) => {
    if (!email) return '';
    const match = email.match(/<([^>]+)>/);
    if (match) {
      return match[1].toLowerCase().trim();
    }
    return email.toLowerCase().trim();
  },
  onCreateAction,
  // When true, hides the inline "+ Create" toolbar button. Used when a parent
  // (e.g. CustomerMessageModal) provides its own create dropdown in its header.
  hideInlineCreateButton = false,
}: ConversationViewProps) {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const conversationRootRef = useRef<HTMLDivElement>(null);
  useKeyboardBottomInset(conversationRootRef);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRenderRef = useRef(true);
  
  // Timeline states
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [timelineOffset, setTimelineOffset] = useState(0);
  const [hasMoreTimeline, setHasMoreTimeline] = useState(false);
  const [loadingMoreTimeline, setLoadingMoreTimeline] = useState(false);
  
  // Message input and UI states — restore any persisted SMS draft for this card.
  const [messageInput, setMessageInput] = useState(() =>
    readModalDraft(selectedBusiness?.id, card.id),
  );
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [replyMethod, setReplyMethod] = useState<'sms' | 'email'>(initialReplyMethod);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [newMessageNotification, setNewMessageNotification] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // Mirror the SMS draft to sessionStorage so a full reload restores it. Writing
  // an empty string (e.g. after a successful send) clears the stored draft.
  useEffect(() => {
    writeModalDraft(selectedBusiness?.id, card.id, messageInput);
  }, [messageInput, card.id, selectedBusiness?.id]);

  // Effective z-index for inline overlays (email compose, AI dropdown). Defaults to the
  // historical hardcoded values when ConversationView is rendered at the top level;
  // when rendered inside another modal (e.g. CustomerMessageModal) it stacks above the parent.
  const emailComposeZ = useModalZIndex(100065);
  const aiPromptZ = useModalZIndex(100066);

  // Snippets modal state
  const [localSnippetsModalOpen, setLocalSnippetsModalOpen] = useState(false);
  
  // Create modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  
  // Email compose modal state
  const [emailComposeModalOpen, setEmailComposeModalOpen] = useState(false);
  const [replySubject, setReplySubject] = useState<string>('');
  const [isReplyMode, setIsReplyMode] = useState(false);
  
  // AI state
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiError, setAIError] = useState<string | null>(null);
  const [aiMode, setAIMode] = useState<'compose' | 'autocomplete' | 'polish' | null>(null);
  const [aiPrompt, setAIPrompt] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [currentEmailSubject, setCurrentEmailSubject] = useState('');
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  
  const { open: openImageViewer } = useImageViewer();
  
  // Store email insertion function in a ref
  const emailInsertRef = useRef<((content: string) => void) | null>(null);
  const emailSubjectInsertRef = useRef<((content: string) => void) | null>(null);
  
  // Debug logging for AI dropdown state
  useEffect(() => {
    if (emailComposeModalOpen) {
      console.log('🔍 AI Dropdown State:', {
        emailComposeModalOpen,
        showAIPrompt,
        emailBodyLength: emailBody.length,
        emailBodyTrim: emailBody.trim().length,
        aiMode,
        shouldShow: emailComposeModalOpen && showAIPrompt && emailBody.trim() && !aiMode
      });
    }
  }, [emailComposeModalOpen, showAIPrompt, emailBody, aiMode]);

  // Update replyMethod when initialReplyMethod prop changes
  useEffect(() => {
    setReplyMethod(initialReplyMethod);
  }, [initialReplyMethod]);
  
  const displayName = card.customer_name || card.contact_info;
  const TIMELINE_PAGE_SIZE = 15;

  // Memoized customer lookup
  const resolvedCustomerIds = useMemo(
    () => resolveCustomerIdsForContact(card.customer_id, card.contact_info, customers),
    [card.customer_id, card.contact_info, customers],
  );

  const currentCustomer = useMemo(() => {
    if (resolvedCustomerIds.length === 0) return null;
    return customers.find((customer) => customer.id === resolvedCustomerIds[0]) || null;
  }, [resolvedCustomerIds, customers]);

  const [openOpportunities, setOpenOpportunities] = useState<OpenOpportunitySummary[]>([]);
  // Opens the CRM opportunity in-place over the conversation (no route change).
  const [embeddedOpportunityId, setEmbeddedOpportunityId] = useState<number | null>(null);

  const handleLeadFollowUpSave = useCallback(
    async (opportunityId: number, followUpAt: string | null) => {
      if (!selectedBusiness?.id) return;
      const opp = openOpportunities.find((o) => o.id === opportunityId);
      try {
        await scheduleOpportunityFollowUp({
          businessId: selectedBusiness.id,
          opportunityId,
          customerId: opp?.customer_id ?? currentCustomer?.id ?? null,
          followUpAt,
          previousFollowUpAt: opp?.next_follow_up_at ?? null,
        });
        setOpenOpportunities((prev) =>
          prev.map((o) =>
            o.id === opportunityId ? { ...o, next_follow_up_at: followUpAt } : o,
          ),
        );
        toast.success(followUpAt ? 'Follow-up scheduled' : 'Follow-up cleared');
      } catch (error) {
        console.error('Error saving lead follow-up:', error);
        toast.error('Could not save follow-up');
      }
    },
    [selectedBusiness?.id, openOpportunities, currentCustomer?.id],
  );

  const handleLeadFollowUpComplete = useCallback(
    async (opportunityId: number) => {
      if (!selectedBusiness?.id) return;
      const opp = openOpportunities.find((o) => o.id === opportunityId);
      if (!opp?.next_follow_up_at) return;
      try {
        await markOpportunityFollowUpComplete({
          businessId: selectedBusiness.id,
          opportunityId,
          customerId: opp.customer_id ?? currentCustomer?.id ?? null,
          previousFollowUpAt: opp.next_follow_up_at,
        });
        setOpenOpportunities((prev) =>
          prev.map((o) =>
            o.id === opportunityId ? { ...o, next_follow_up_at: null } : o,
          ),
        );
        toast.success('Follow-up marked complete');
      } catch (error) {
        console.error('Error completing lead follow-up:', error);
        toast.error('Could not complete follow-up');
      }
    },
    [selectedBusiness?.id, openOpportunities, currentCustomer?.id],
  );

  const handleLeadStageChange = useCallback(
    async (opportunityId: number, newStageId: number) => {
      if (!selectedBusiness?.id) return;
      const opp = openOpportunities.find((o) => o.id === opportunityId);
      if (!opp || opp.stage_id === newStageId) return;
      const oldStageId = opp.stage_id;
      const oldStageName = opp.stage_name;
      const newStageName = opp.stages.find((s) => s.id === newStageId)?.name ?? '';
      setOpenOpportunities((prev) =>
        prev.map((o) =>
          o.id === opportunityId
            ? { ...o, stage_id: newStageId, stage_name: newStageName }
            : o,
        ),
      );
      try {
        await moveOpportunityToStage({
          businessId: selectedBusiness.id,
          opportunityId,
          customerId: opp.customer_id ?? currentCustomer?.id ?? null,
          oldStageId,
          newStageId,
          oldStageName,
          newStageName,
        });
        toast.success(newStageName ? `Moved to ${newStageName}` : 'Stage updated');
      } catch (error) {
        console.error('Error changing lead stage:', error);
        setOpenOpportunities((prev) =>
          prev.map((o) =>
            o.id === opportunityId
              ? { ...o, stage_id: oldStageId, stage_name: oldStageName }
              : o,
          ),
        );
        toast.error('Could not change stage');
      }
    },
    [selectedBusiness?.id, openOpportunities, currentCustomer?.id],
  );

  useEffect(() => {
    if (!selectedBusiness?.id || resolvedCustomerIds.length === 0) {
      setOpenOpportunities([]);
      return;
    }

    let cancelled = false;

    const loadOpenOpportunities = async () => {
      const opportunities = await getOpenOpportunitiesForCustomerIds(
        selectedBusiness.id,
        resolvedCustomerIds,
      );
      if (!cancelled) {
        setOpenOpportunities(opportunities);
      }
    };

    void loadOpenOpportunities();

    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id, resolvedCustomerIds.join(',')]);

  // Refresh the Lead banner immediately after a lead is created from the "+" menu.
  useEffect(() => {
    const handler = () => {
      if (!selectedBusiness?.id || resolvedCustomerIds.length === 0) return;
      getOpenOpportunitiesForCustomerIds(selectedBusiness.id, resolvedCustomerIds)
        .then(setOpenOpportunities)
        .catch(() => {});
    };
    window.addEventListener(LEAD_CREATED_EVENT, handler);
    return () => window.removeEventListener(LEAD_CREATED_EVENT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.id, resolvedCustomerIds.join(',')]);
  
  // Replace variables in snippet with customer + open lead data (shared helper).
  // Lead variables fill from the most recent open lead; when there is none they
  // are blanked and we warn so the user can adjust before sending.
  const replaceSnippetVariables = (content: string) => {
    const { text, missingLeadVariables } = substituteSnippetVariables(content, {
      customer: currentCustomer,
      lead: openOpportunities[0] || null,
    });
    if (missingLeadVariables.length > 0) {
      toast(`No open lead for this customer — ${missingLeadVariables.join(', ')} left blank`, { icon: '⚠️' });
    }
    return text;
  };
  
  // Handle snippet selection
  const handleSnippetSelect = (snippet: any) => {
    const processedContent = replaceSnippetVariables(snippet.content);
    
    if (replyMethod === 'email') {
      if (emailInsertRef.current) {
        emailInsertRef.current(processedContent);
      }
    } else {
      setMessageInput(prev => {
        return prev ? `${prev}\n\n${processedContent}` : processedContent;
      });
    }
  };

  // Unified timeline fetching function with proper pagination
  const fetchTimelineItems = async (offset: number = 0, limit: number = TIMELINE_PAGE_SIZE): Promise<{ items: TimelineItem[], hasMore: boolean }> => {
    try {
      if (!selectedBusiness?.id || !supabase) {
        return { items: [], hasMore: false };
      }

      // Core tables: one call returns the page already merged and ordered.
      if (await isCoreMessagesEnabled(selectedBusiness.id)) {
        const page = await fetchCoreThread(selectedBusiness.id, conversationKeyForCard(card), { limit, offset });
        return { items: page.items, hasMore: page.hasMore };
      }

      // Get messages for this conversation with pagination
      let messageQuery = supabase
        .from('all_messages')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      // Apply conversation-specific filters
      if (card.type === 'customer' && card.customer_id) {
        messageQuery = messageQuery.eq('customer_id', card.customer_id);
      } else {
        messageQuery = messageQuery.is('customer_id', null);
        if (card.type === 'phone') {
          messageQuery = messageQuery.eq('message_type', 'sms')
                                   .or(`from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`);
        } else if (card.type === 'email') {
          messageQuery = messageQuery.eq('message_type', 'email')
                                    .or(`from_number.ilike.%${card.contact_info}%,to_number.ilike.%${card.contact_info}%,from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`);
        }
      }

      // Get all milestones for customer conversations
      let allMilestones: BookingMilestone[] = [];
      if (card.type === 'customer' && card.customer_id) {
        try {
          allMilestones = await getCustomerMilestones(card.customer_id);
        } catch (error) {
          console.error('Error fetching milestones:', error);
        }
      }

      // Get call history for this conversation (customer_id + phone variants).
      // Do not require customer_id IS NULL on phone match — CRM triggers may
      // have stamped a different id; phone still wins for display.
      const callQueries: Promise<any>[] = [];
      if (card.type === 'email') {
        callQueries.push(Promise.resolve({ data: [], error: null }));
      } else if (card.type === 'customer' && card.customer_id) {
        callQueries.push(
          supabase
            .from('call_history')
            .select('*')
            .eq('business_id', selectedBusiness.id)
            .eq('customer_id', card.customer_id)
            .order('call_time', { ascending: false })
        );

        const customer = customers.find((c) => c.id === card.customer_id);
        const custPhone = customer?.phone || card.contact_info;
        if (custPhone) {
          const digits = custPhone.replace(/\D/g, '');
          const last10 = digits.length >= 10 ? digits.slice(-10) : digits;
          const phoneCandidates = new Set<string>([custPhone]);
          if (last10.length === 10) {
            phoneCandidates.add(`+1${last10}`);
            phoneCandidates.add(`1${last10}`);
            phoneCandidates.add(last10);
            phoneCandidates.add(`+${last10}`);
          }
          if (digits.length === 11 && digits.startsWith('1')) {
            phoneCandidates.add(`+${digits}`);
            phoneCandidates.add(digits);
          }
          const phoneOrParts = Array.from(phoneCandidates)
            .flatMap((p) => [`from_number.eq.${p}`, `to_number.eq.${p}`]);
          callQueries.push(
            supabase
              .from('call_history')
              .select('*')
              .eq('business_id', selectedBusiness.id)
              .or(phoneOrParts.join(','))
              .order('call_time', { ascending: false })
          );
        }
      } else if (card.type === 'phone') {
        const digits = (card.contact_info || '').replace(/\D/g, '');
        const last10 = digits.length >= 10 ? digits.slice(-10) : digits;
        const phoneCandidates = new Set<string>([card.contact_info].filter(Boolean) as string[]);
        if (last10.length === 10) {
          phoneCandidates.add(`+1${last10}`);
          phoneCandidates.add(`1${last10}`);
          phoneCandidates.add(last10);
          phoneCandidates.add(`+${last10}`);
        }
        const phoneOrParts = Array.from(phoneCandidates)
          .flatMap((p) => [`from_number.eq.${p}`, `to_number.eq.${p}`]);
        callQueries.push(
          supabase
            .from('call_history')
            .select('*')
            .eq('business_id', selectedBusiness.id)
            .or(phoneOrParts.join(','))
            .order('call_time', { ascending: false })
        );
      }

      const [messagesResult, ...callResults] = await Promise.all([
        messageQuery,
        ...callQueries,
      ]);

      if (messagesResult.error) throw messagesResult.error;

      const allMessages = messagesResult.data || [];
      const seenCallIds = new Set<string>();
      const allCalls: any[] = [];
      for (const result of callResults) {
        if (result.error) {
          console.error('Error fetching calls:', result.error);
          continue;
        }
        for (const call of result.data || []) {
          if (!seenCallIds.has(call.id)) {
            seenCallIds.add(call.id);
            allCalls.push(call);
          }
        }
      }
      
      const hasMoreMessages = allMessages.length === limit;

      // Combine messages, calls, and milestones into unified timeline
      const combinedTimeline: TimelineItem[] = [];

      // Add messages to timeline
      allMessages.forEach(message => {
        const mappedMessage: Message = {
          ...message,
          body: (message as any).body || (message as any).content || '',
          media_urls: (message as any).media_urls 
            ? (Array.isArray((message as any).media_urls) 
                ? (message as any).media_urls 
                : JSON.parse((message as any).media_urls || '[]'))
            : null
        };
        combinedTimeline.push({
          id: `message_${message.id}`,
          type: 'message',
          created_at: message.created_at,
          message: mappedMessage
        });
      });

      // Add calls to timeline (convert to message format)
      allCalls.forEach(call => {
        let matchedCustomerId = call.customer_id;
        
        if (!matchedCustomerId) {
          const callPhoneNumber = call.direction === 'inbound' ? call.from_number : call.to_number;
          const matchedCustomer = customers.find(customer => {
            if (!customer.phone) return false;
            const customerPhone = customer.phone.replace(/\D/g, '');
            const callPhone = callPhoneNumber.replace(/\D/g, '');
            return customerPhone === callPhone || 
                   customerPhone === callPhone.substring(1) || 
                   `1${customerPhone}` === callPhone;
          });
          
          if (matchedCustomer) {
            matchedCustomerId = matchedCustomer.id;
          }
        }

        const callMessage: Message = {
          id: call.id,
          business_id: call.business_id,
          customer_id: matchedCustomerId,
          message_type: 'call',
          direction: call.direction,
          from_number: call.from_number,
          to_number: call.to_number,
          body: call.direction === 'outbound' ? 'Outbound call' : 
            (call.status === 'answered' ? 'Call answered' : 'Missed call'),
          read: 'read',
          created_at: call.call_time,
          updated_at: call.updated_at || call.call_time,
          call_status: call.status,
          duration_seconds: call.duration_seconds,
          call_sid: call.call_sid,
          viewed: call.viewed,
          recording_url: call.recording_url || null,
          transcription_text: call.transcription_text || null,
          transcription_status: call.transcription_status || null
        };

        combinedTimeline.push({
          id: `call_${call.id}`,
          type: 'message',
          created_at: call.call_time,
          message: callMessage
        });
      });

      // Add milestones to timeline
      allMilestones.forEach(milestone => {
        combinedTimeline.push({
          id: `milestone_${milestone.id}`,
          type: 'milestone',
          created_at: milestone.created_at,
          milestone
        });
      });

      // Sort timeline by created_at (newest first)
      combinedTimeline.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      return { items: combinedTimeline, hasMore: hasMoreMessages };
    } catch (error) {
      console.error('Error fetching timeline items:', error);
      return { items: [], hasMore: false };
    }
  };

  // Load initial timeline items when conversation opens
  useEffect(() => {
    const loadInitialTimeline = async () => {
      const result = await fetchTimelineItems(0, TIMELINE_PAGE_SIZE);
      
      // Reverse items for display (oldest first in UI)
      const reversedItems = [...result.items].reverse();
      setTimelineItems(reversedItems);
      setTimelineOffset(TIMELINE_PAGE_SIZE);
      setHasMoreTimeline(result.hasMore);
      
      // Ensure scroll to bottom after timeline loads
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
        }
      }, 100);
    };

    loadInitialTimeline();
  }, [card.id, selectedBusiness?.id]);

  // Load more timeline items function
  const loadMoreTimeline = async () => {
    if (loadingMoreTimeline || !hasMoreTimeline) return;
    
    setLoadingMoreTimeline(true);
    
    try {
      const result = await fetchTimelineItems(timelineOffset, TIMELINE_PAGE_SIZE);
      
      if (result.items.length > 0) {
        const reversedNewItems = [...result.items].reverse();
        setTimelineItems(prev => [...reversedNewItems, ...prev]);
        setTimelineOffset(prev => prev + TIMELINE_PAGE_SIZE);
        setHasMoreTimeline(result.hasMore);
      } else {
        setHasMoreTimeline(false);
      }
    } catch (error) {
      console.error('Error loading more timeline items:', error);
    } finally {
      setLoadingMoreTimeline(false);
    }
  };

  // Refresh timeline (reload from beginning)
  const refreshTimeline = async () => {
    try {
      const result = await fetchTimelineItems(0, TIMELINE_PAGE_SIZE);
      
      // Reverse items for display (oldest first in UI)
      const reversedItems = [...result.items].reverse();
      setTimelineItems(reversedItems);
      setTimelineOffset(TIMELINE_PAGE_SIZE);
      setHasMoreTimeline(result.hasMore);
      
      // Scroll to bottom to show newest message
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 100);
    } catch (error) {
      console.error('Error refreshing timeline:', error);
    }
  };

  // Consolidated conversation initialization when card changes
  useEffect(() => {
    isFirstRenderRef.current = true;
    setNewMessageNotification(false);
    setShowScrollToBottom(false);
    setLoadingMoreTimeline(false);
    // Restore the new card's persisted draft instead of blanking the composer.
    setMessageInput(readModalDraft(selectedBusiness?.id, card.id));
    setSendError(null);
    // Use initialReplyMethod prop if provided, otherwise fall back to last message type
    if (initialReplyMethod) {
      setReplyMethod(initialReplyMethod);
    } else {
      // Default to email if last message was email, otherwise SMS
      setReplyMethod(card.last_message.message_type === 'email' ? 'email' : 'sms');
    }
    
    // Mark as not first render after a brief delay
    setTimeout(() => {
      isFirstRenderRef.current = false;
    }, 100);
  }, [card.id, card.last_message.message_type, initialReplyMethod]);

  // Handle scroll to detect if user is at top
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isNearTop = container.scrollTop <= 100;
      
      if (isNearTop) {
        setShowScrollToBottom(false);
      } else {
        setShowScrollToBottom(true);
      }
    }
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = 0;
      setShowScrollToBottom(false);
    }
  };

  // Add scroll listener to messages container
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Handle typing detection for SMS input
  const handleMessageInputChange = (value: string) => {
    setMessageInput(value);
    setIsUserTyping(true);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      setIsUserTyping(false);
    }, 2000);
  };

  // Clear error when user starts typing
  useEffect(() => {
    if (messageInput && sendError) {
      setSendError(null);
    }
  }, [messageInput, sendError]);

  // Handle AI SMS reply
  const handleAISMSReply = async () => {
    console.log('📱 AI SMS Reply triggered');
    
    setIsGeneratingAI(true);
    setAIError(null);

    try {
      // Get SMS messages from timeline
      const smsMessages = timelineItems
        .filter(item => item.type === 'message' && item.message?.message_type === 'sms')
        .map(item => item.message!)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      if (smsMessages.length === 0) {
        throw new Error('No SMS messages found in this conversation');
      }

      const mostRecentMessage = smsMessages[smsMessages.length - 1];

      // Get milestones for context
      const conversationMilestones = timelineItems
        .filter(item => item.type === 'milestone')
        .map(item => ({
          id: item.id,
          type: item.milestone?.type || 'unknown',
          title: item.milestone?.title || '',
          description: item.milestone?.description,
          created_at: item.created_at,
          booking_id: item.milestone?.booking_id,
          proposal_id: item.milestone?.proposal_id
        }));

      console.log('🤖 Sending AI SMS reply request with', smsMessages.length, 'messages and', conversationMilestones.length, 'milestones');

      const { data, error } = await supabase.functions.invoke('ai-sms-reply', {
        body: {
          businessId: selectedBusiness?.id,
          businessName: selectedBusiness?.business_name || undefined,
          customerId: card.customer_id,
          customerName: card.customer_name || card.contact_info,
          recentMessage: {
            id: mostRecentMessage.id,
            direction: mostRecentMessage.direction,
            body: mostRecentMessage.body,
            created_at: mostRecentMessage.created_at
          },
          additionalContext: card.type === 'customer' ? 'existing customer' : 'new contact',
          milestones: conversationMilestones
        }
      });

      if (error) throw error;

      if (data?.reply) {
        console.log('📱 AI SMS Reply generated:', data);
        setMessageInput(data.reply);
        setAIError(null);
      } else {
        throw new Error('No reply generated');
      }
    } catch (error) {
      console.error('Error generating AI SMS reply:', error);
      setAIError(error instanceof Error ? error.message : 'Failed to generate AI reply');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Handle AI email generation
  const handleGenerateAIEmail = async (mode: 'compose' | 'autocomplete' | 'polish') => {
    console.log('🤖 AI Generate called with mode:', mode);
    console.log('🤖 Current emailBody:', emailBody);
    console.log('🤖 Current emailSubject:', currentEmailSubject);
    console.log('🤖 Has subject?', currentEmailSubject.trim().length > 0);
    
    let promptToUse = '';
    
    // Build the prompt based on the mode
    if (mode === 'compose') {
      if (!aiPrompt.trim()) {
        setAIError('Please enter a prompt');
        return;
      }
      promptToUse = aiPrompt;
    } else if (mode === 'autocomplete') {
      promptToUse = `Continue and complete this email message: "${emailBody}"`;
      console.log('🤖 Auto-complete prompt:', promptToUse);
    } else if (mode === 'polish') {
      // Polish can have optional instructions
      if (aiPrompt.trim()) {
        promptToUse = `Polish and enhance this email message with the following instructions: "${aiPrompt}". Original message: "${emailBody}"`;
      } else {
        promptToUse = `Polish and enhance this email message, making it more professional and clear: "${emailBody}"`;
      }
      console.log('🤖 Polish prompt:', promptToUse);
    }

    setIsGeneratingAI(true);
    setAIError(null);

    try {
      const { data, error } = await supabase.functions.invoke('ai-compose-email', {
        body: {
          prompt: promptToUse,
          customerName: card.customer_name || undefined,
          businessName: selectedBusiness?.business_name || undefined,
          context: card.type === 'customer' ? 'existing customer' : 'new contact',
          mode: mode,
          hasSubject: currentEmailSubject.trim().length > 0,
          businessId: selectedBusiness?.id
        }
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to generate email');
      }

      console.log('🤖 AI Response:', data);

      // Insert the generated email into the composer
      if (emailInsertRef.current) {
        if (mode === 'autocomplete') {
          emailInsertRef.current(data.email, true);
        } else if (mode === 'polish') {
          emailInsertRef.current(data.email, true);
        } else {
          emailInsertRef.current(data.email, true);
        }
      }

      // If AI generated a subject, insert it
      if (data.subject && emailSubjectInsertRef.current) {
        console.log('🤖 Inserting AI generated subject:', data.subject);
        emailSubjectInsertRef.current(data.subject);
      }

      // Reset AI prompt state
      setShowAIPrompt(false);
      setAIPrompt('');
      setAIMode(null);
    } catch (error) {
      console.error('Error generating AI email:', error);
      setAIError(error instanceof Error ? error.message : 'Failed to generate email');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Handle SMS sending
  const handleSendSMS = async (aiGenerated: boolean = false, mediaFiles?: File[]) => {
    if (!messageInput.trim() && (!mediaFiles || mediaFiles.length === 0)) {
      setSendError('Please enter a message or attach a photo');
      return;
    }

    if (!selectedBusiness?.id) {
      setSendError('No business selected');
      return;
    }

    let phoneNumber = '';
    if (card.type === 'customer') {
      phoneNumber = currentCustomer?.phone || '';
    } else if (card.type === 'phone') {
      phoneNumber = card.contact_info;
    }

    if (!phoneNumber) {
      setSendError('No phone number available for this contact');
      return;
    }

    setIsSending(true);
    setSendError(null);

    // Who is sending (cached after the first call), so the bubble shows the right avatar immediately.
    const currentSender = await resolveCurrentSender(selectedBusiness.id);

    // Optimistic UI — inject the message into the timeline immediately
    const optimisticId = `optimistic_${Date.now()}`;
    const optimisticItem: TimelineItem = {
      id: `message_${optimisticId}`,
      type: 'message',
      created_at: new Date().toISOString(),
      message: {
        id: optimisticId,
        business_id: selectedBusiness.id,
        customer_id: card.customer_id || null,
        message_type: 'sms',
        direction: 'outbound',
        from_number: '',
        to_number: phoneNumber,
        body: messageInput.trim(),
        read: 'read',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ai_response: aiGenerated,
        media_urls: null,
        sender: currentSender,
      },
    };

    // timelineItems is stored oldest-first; append to show at bottom
    setTimelineItems(prev => [...prev, optimisticItem]);
    const savedInput = messageInput.trim();
    setMessageInput('');

    try {
      const result = await sendSMS({
        businessId: selectedBusiness.id,
        to: phoneNumber,
        message: savedInput,
        aiGenerated,
        mediaFiles,
        customerId: card.customer_id || undefined
      });

      if (result.success) {
        // A text reply clears the phone side (texts, missed calls, callbacks)
        // and leaves any unread email threads alone.
        if (markAllMessagesAsRead) {
          markAllMessagesAsRead(card, PHONE_READ_KINDS);
        }
      } else {
        // Remove optimistic message and restore input on failure
        setTimelineItems(prev => prev.filter(item => item.id !== `message_${optimisticId}`));
        setMessageInput(savedInput);
        setSendError(result.error || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      setTimelineItems(prev => prev.filter(item => item.id !== `message_${optimisticId}`));
      setMessageInput(savedInput);
      setSendError(error instanceof Error ? error.message : 'Failed to send SMS');
    } finally {
      setIsSending(false);
    }
  };

  // Every email in the same thread as `message` (grouped by normalised subject,
  // the same grouping the timeline uses to collapse a thread into one bubble).
  const emailThreadFor = (message: Message): Message[] => {
    const emailMessages = card.messages.filter((msg) => msg.message_type === 'email');
    const fallbackSubject = emailMessages.find((msg) => msg.subject)?.subject || '';
    const threadSubject = normalizeEmailSubject(message.subject || card.last_message.subject || fallbackSubject);
    if (!threadSubject) return emailMessages;
    return emailMessages.filter((msg) => normalizeEmailSubject(msg.subject || '') === threadSubject);
  };

  const unreadIdsIn = (messages: Message[]): string[] =>
    messages
      .filter((m) => m.direction !== 'outbound' && m.read === 'unread' && !locallyReadMessages.has(m.id))
      .map((m) => m.id);

  const markManyRead = async (ids: string[]) => {
    if (ids.length === 0) return;
    if (markMessagesAsRead) {
      await markMessagesAsRead(ids);
    } else {
      await Promise.all(ids.map((id) => markMessageAsRead(id)));
    }
  };

  // Handle email sending success
  const handleEmailSendSuccess = async (messageId: string, details?: { subject: string; body: string; to: string }) => {
    setSendError(null);
    // Replying to a thread marks that thread read and nothing else. A brand-new
    // email starts its own thread, so nothing is marked.
    if (isReplyMode && replySubject) {
      const threadSubject = normalizeEmailSubject(replySubject);
      const threadIds = unreadIdsIn(
        card.messages.filter((msg) => msg.message_type === 'email' && normalizeEmailSubject(msg.subject || '') === threadSubject)
      );
      void markManyRead(threadIds);
    }

    if (details && selectedBusiness?.id) {
      const optimisticItem: TimelineItem = {
        id: `message_${messageId}`,
        type: 'message',
        created_at: new Date().toISOString(),
        message: {
          id: messageId,
          business_id: selectedBusiness.id,
          customer_id: card.customer_id || null,
          message_type: 'email',
          direction: 'outbound',
          from_number: '',
          to_number: details.to,
          body: details.body,
          subject: details.subject,
          read: 'read',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          media_urls: null,
        },
      };
      setTimelineItems(prev => [...prev, optimisticItem]);
    } else {
      await refreshTimeline();
    }
  };

  // Handle email sending error
  const handleEmailSendError = (error: string) => {
    setSendError(error);
  };

  // Get email address for sending
  const getEmailAddress = () => {
    if (card.type === 'customer') {
      return currentCustomer?.email || '';
    } else if (card.type === 'email') {
      return card.contact_info;
    }
    return '';
  };

  // Generate default email subject based on conversation
  const getDefaultEmailSubject = () => {
    const emailMessages = timelineItems
      .filter(item => item.type === 'message' && item.message?.message_type === 'email' && item.message?.subject)
      .map(item => item.message!)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    
    const lastEmailMessage = emailMessages[0];
    
    if (lastEmailMessage?.subject) {
      const subject = decodeEmailSubject(lastEmailMessage.subject);
      return subject.replace(/^(re:|fwd:|fw:)\s*/gi, '').trim() || subject;
    }
    
    return 'Reply from your booking';
  };

  // Get available reply methods
  const getAvailableReplyMethods = () => {
    if (card.type === 'customer') {
      const methods = [];
      if (currentCustomer?.phone) methods.push('sms');
      if (currentCustomer?.email) methods.push('email');
      return methods;
    } else if (card.type === 'phone') {
      return ['sms'];
    } else if (card.type === 'email') {
      return ['email'];
    }
    return [];
  };

  const availableReplyMethods = getAvailableReplyMethods();
  const canSms = availableReplyMethods.includes('sms');
  const canEmail = availableReplyMethods.includes('email');

  // Update reply method if current selection is not available
  useEffect(() => {
    if (availableReplyMethods.length > 0 && !availableReplyMethods.includes(replyMethod)) {
      if (availableReplyMethods.includes(card.last_message.message_type)) {
        setReplyMethod(card.last_message.message_type);
      } else {
        setReplyMethod(availableReplyMethods[0] as 'sms' | 'email');
      }
    }
  }, [card.id, availableReplyMethods, replyMethod, card.last_message.message_type]);

  const emailAddress = getEmailAddress();
  const emailSubject = getDefaultEmailSubject();
  const isEmailDisabled = !selectedBusiness?.id || !emailAddress;

  // Default handlers
  const defaultHandleEditCustomer = (customer: Customer) => {
    if (onEditCustomer) {
      onEditCustomer(customer);
    }
  };

  const defaultHandleCreateCustomer = (card: CommunicationCard) => {
    if (onCreateCustomer) {
      onCreateCustomer(card);
    }
  };

  const defaultHandleMilestoneClick = async (milestone: BookingMilestone) => {
    if (onMilestoneClick) {
      onMilestoneClick(milestone);
    }
  };

  const defaultMarkAllMessagesAsRead = async (card: CommunicationCard) => {
    if (markAllMessagesAsRead) {
      await markAllMessagesAsRead(card);
    }
  };

  return (
    <div
      ref={conversationRootRef}
      className="messages-conversation-view flex flex-col h-full bg-white overflow-hidden"
    >
      {/* Conversation Header - iOS Style (z-10 so back button stays tappable on iOS) */}
      <div className="flex-shrink-0 relative z-10 px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3 sm:space-x-4">
          {onMobileBack && (
            <button
              type="button"
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMobileBack();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMobileBack();
              }}
              className="md:hidden p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-[0.98] flex-shrink-0 touch-manipulation cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center -ml-1"
              style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
              aria-label="Back to conversations"
            >
              <span className="sr-only">Back to conversations</span>
              ←
            </button>
          )}
          
          <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-xl flex items-center justify-center shadow-sm border border-gray-200">
            {card.type === 'customer' ? (
              <User className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
            ) : card.type === 'phone' ? (
              card.last_message.message_type === 'call' ? (
                <Phone className={`h-5 w-5 sm:h-6 sm:w-6 ${
                  card.last_message.direction === 'outbound' ? 'text-blue-500' : 
                  (card.last_message.call_status === 'answered' ? 'text-green-500' : 'text-red-500')
                }`} />
              ) : (
                <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500" />
              )
            ) : (
              <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            {card.type === 'customer' ? (
              <>
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <button
                    onClick={() => {
                      if (currentCustomer) {
                        defaultHandleEditCustomer(currentCustomer);
                        return;
                      }
                      if (card.customer_id) {
                        const fullCustomer = customers.find(c => c.id === card.customer_id);
                        if (fullCustomer) {
                          defaultHandleEditCustomer(fullCustomer);
                        }
                      }
                    }}
                    className="text-base sm:text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors cursor-pointer truncate"
                    title="View customer details"
                  >
                    {displayName}
                  </button>
                  {currentCustomer?.is_supplier && (
                    <span className="hidden sm:flex items-center space-x-1 bg-orange-100 text-orange-700 text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0">
                      <Building2 className="h-3.5 w-3.5" />
                      <span>Supplier</span>
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                    {card.type === 'email' ? extractEmailAddress(card.contact_info) : card.contact_info}
                  </h3>
                  {card.type === 'email' && muteEmailSender && (
                    <button
                      onClick={async () => {
                        if (window.confirm(`Mark ${extractEmailAddress(card.contact_info)} as junk? All future emails from this sender will be hidden from your unread pipeline.`)) {
                          try {
                            await muteEmailSender(card.contact_info);
                            // Optionally close the conversation or show a success message
                          } catch (error) {
                            console.error('Error muting sender:', error);
                            alert('Failed to mark sender as junk. Please try again.');
                          }
                        }
                      }}
                      className="p-2 hover:bg-red-50 rounded-xl transition-all duration-200 active:scale-[0.98]"
                      title="Mark as Junk"
                    >
                      <Ban className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 hover:text-red-700" />
                    </button>
                  )}
                  {onCreateCustomer && (
                    <button
                      onClick={() => defaultHandleCreateCustomer(card)}
                      className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-[0.98]"
                      title="Create customer"
                    >
                      <Plus className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 hover:text-gray-700" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          
          {onClose && !hideCloseButton && (
            <button
              onClick={onClose}
              className="hidden md:block p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-[0.98] flex-shrink-0"
            >
              <span className="sr-only">Close conversation</span>
              ✕
            </button>
          )}
        </div>
      </div>

      <ConversationLeadBanner
        opportunities={openOpportunities}
        onOpenOpportunity={(opportunityId) => setEmbeddedOpportunityId(opportunityId)}
        onSaveFollowUp={handleLeadFollowUpSave}
        onCompleteFollowUp={handleLeadFollowUpComplete}
        onChangeStage={handleLeadStageChange}
      />

      {embeddedOpportunityId !== null ? (
        <CrmEmbeddedOpportunityModal
          opportunityId={embeddedOpportunityId}
          onClose={() => setEmbeddedOpportunityId(null)}
          onUpdated={() => {
            if (!selectedBusiness?.id || resolvedCustomerIds.length === 0) return;
            getOpenOpportunitiesForCustomerIds(selectedBusiness.id, resolvedCustomerIds)
              .then(setOpenOpportunities)
              .catch(() => {});
          }}
        />
      ) : null}

      {/* Messages - Scrollable with iOS Style */}
      <div ref={messagesContainerRef} className="messages-scrollable flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-6 sm:py-6 relative flex flex-col-reverse">
        {isFirstRenderRef.current && timelineItems.length === 0 && (
          <div className="space-y-6 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={`skeleton-${i}`} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs lg:max-w-md px-5 py-4 rounded-2xl ${
                  i % 2 === 0 ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <div className="space-y-3">
                    <div className="h-3 bg-gray-300 rounded w-16"></div>
                    <div className="h-4 bg-gray-300 rounded w-full"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-300 rounded w-20"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {(!isFirstRenderRef.current || timelineItems.length > 0) && (
          <>
            {newMessageNotification && (
              <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10 bg-blue-500 text-white px-5 py-3 rounded-2xl shadow-lg animate-pulse">
                <div className="flex items-center space-x-2">
                  <MessageCircle className="h-5 w-5" />
                  <span className="text-sm font-semibold">New message received</span>
                </div>
              </div>
            )}
            
            {/* Timeline items in reverse order */}
            {[...timelineItems].reverse().map((item) => {
              if (item.type === 'message' && item.message) {
                const message = item.message;
                const isOutbound = message.direction === 'outbound';
                const messageTime = new Date(message.created_at).toLocaleString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                });
                
                // An email bubble stands for its whole thread, so it is unread if
                // any inbound message in the thread is unread, not just the latest.
                const isUnread = message.message_type === 'email'
                  ? unreadIdsIn(emailThreadFor(message)).length > 0
                  : !isOutbound && (
                    (message.read === 'unread' && !locallyReadMessages.has(message.id)) ||
                    (message.message_type === 'call' && message.call_status === 'missed' && message.viewed === false)
                  );
                
                return (
                  <div
                    key={item.id}
                    className={`flex items-end gap-1.5 ${isOutbound ? 'justify-end' : 'justify-start'} mb-4 sm:mb-6`}
                  >
                    {!isOutbound && (
                      <SenderBadge
                        businessId={selectedBusiness?.id}
                        sender={senderOf(message)}
                        direction="inbound"
                        customerName={card.customer_name || null}
                      />
                    )}
                    <div className={`relative ${message.message_type === 'email' ? 'flex-shrink-0 max-w-[90%] sm:max-w-[85%] lg:max-w-[75%]' : 'flex-shrink-0'}`}>
                      <div
                        className={`${
                          message.message_type === 'email'
                            ? 'w-full px-0 py-0 rounded-none shadow-none bg-transparent border-0'
                            : 'max-w-[280px] sm:max-w-xs lg:max-w-md px-4 py-3 sm:px-5 sm:py-4 rounded-2xl shadow-sm'
                        } ${
                          message.message_type === 'email'
                            ? ''
                            : (isOutbound
                            ? 'bg-blue-500 text-white'
                                : 'bg-white text-gray-900 border border-gray-200')
                        }`}
                      >
                        {/* Message Type & Subject - Only show for non-email messages */}
                        {message.message_type !== 'email' && (
                          <>
                        <div className="flex items-center space-x-1.5 sm:space-x-2 mb-1.5 sm:mb-2">
                          {message.message_type === 'sms' ? (
                            <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : message.message_type === 'call' ? (
                            <Phone className={`h-3 w-3 sm:h-4 sm:w-4 ${
                              message.direction === 'outbound' ? 'text-white' : 
                              (message.call_status === 'answered' ? 'text-green-500' : 'text-red-500')
                            }`} />
                              ) : null}
                          <span className="text-xs font-semibold uppercase tracking-wider">
                            {message.message_type === 'call' ? 
                              (message.direction === 'outbound' ? 'Call' : 
                               (message.call_status === 'answered' ? 'Call' : 'Missed')) : 
                              message.message_type}
                          </span>
                          {isOutbound && message.ai_response && (
                            <span className="bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 text-xs font-semibold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full border border-blue-200">
                              AI
                            </span>
                          )}
                        </div>
                        
                            {message.subject && (
                          <div className="font-semibold text-sm sm:text-base mb-1.5 sm:mb-2">
                            {message.subject}
                          </div>
                            )}
                          </>
                        )}
                        
                        {message.message_type === 'email' ? (
                          <>
                            {(() => {
                              const subjectForDisplay = message.subject ? decodeEmailSubject(message.subject).replace(/^(re:|fwd:|fw:)\s*/gi, '').trim() : undefined;
                              const emailAttachments = message.twilio_message_sid
                                ? (
                                    emailAttachmentsByGmailId[message.twilio_message_sid] ||
                                    timelineAttachmentsByGmailId[message.twilio_message_sid] ||
                                    []
                                  )
                                : [];
                              const inlineAttachments = emailAttachments.filter((attachment) => attachment.is_inline);
                              const downloadableAttachments = emailAttachments.filter((attachment) => !attachment.is_inline);
                              const attachmentsForDisplay = emailAttachments.filter(
                                (attachment) => !attachment.is_inline || Boolean(attachment.filename)
                              );
                              const threadMessages = emailThreadFor(message);
                              
                              return (
                                <>
                          <EmailDisplay
                            content={message.body}
                                    subject={subjectForDisplay}
                            createdAt={message.created_at}
                            className="text-xs sm:text-sm"
                            isOutbound={isOutbound}
                            compactView={true}
                            replyTo={emailAddress}
                            replySubject={emailSubject}
                            businessId={selectedBusiness?.id || ''}
                            conversationId={card.id}
                            customerId={card.customer_id}
                            onSendSuccess={handleEmailSendSuccess}
                            onSendError={handleEmailSendError}
                            isEmailDisabled={isEmailDisabled}
                            onReplyClick={(replyTo, replySubject) => {
                              setReplySubject(replySubject);
                              setIsReplyMode(true);
                              setEmailComposeModalOpen(true);
                            }}
                                    inlineAttachments={inlineAttachments.map((attachment) => ({
                                      content_id: null, // EmailAttachment doesn't have content_id field
                                      public_url: attachment.public_url
                                    }))}
                                    attachments={attachmentsForDisplay}
                                    threadMessages={threadMessages}
                                    locallyReadMessages={locallyReadMessages}
                                    onMarkAsRead={(ids) => { void markManyRead(ids); }}
                                  />
                                </>
                              );
                            })()}
                          </>
                        ) : message.message_type === 'call' ? (
                          <div className="text-xs sm:text-sm leading-relaxed">
                            <span>{message.body}</span>
                            {message.call_status === 'answered' && message.duration_seconds != null && message.duration_seconds >= 0 && (
                              <span className="ml-1.5 opacity-90">
                                ({Math.floor(message.duration_seconds / 60)}:{(message.duration_seconds % 60).toString().padStart(2, '0')})
                              </span>
                            )}
                          </div>
                        ) : (
                          <>
                            {message.body && (
                              <div className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words mb-2">
                                {message.body}
                              </div>
                            )}
                            {message.media_urls && Array.isArray(message.media_urls) && message.media_urls.length > 0 && (
                              <div className="space-y-2 mt-2">
                                {message.media_urls.map((mediaUrl, idx) => {
                                  const isTwilioUrl = mediaUrl.includes('api.twilio.com');
                                  const isSupabaseUrl = mediaUrl.includes('supabase.co') || mediaUrl.includes('supabase');
                                  
                                  let proxyUrl: string;
                                  if (isTwilioUrl) {
                                    const mediaPath = mediaUrl.replace('https://api.twilio.com', '');
                                    proxyUrl = selectedBusiness?.id
                                      ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twilio-media-proxy?path=${encodeURIComponent(mediaPath)}&businessId=${selectedBusiness.id}`
                                      : mediaUrl;
                                  } else if (isSupabaseUrl) {
                                    proxyUrl = mediaUrl;
                                  } else {
                                    proxyUrl = mediaUrl;
                                  }
                                  
                                  const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(mediaUrl);
                                  const mightBeImage = isImage || mediaUrl.includes('/Media/') || isSupabaseUrl;
                                  
                                  return (
                                    <MediaDisplayComponent
                                      key={idx}
                                      proxyUrl={proxyUrl}
                                      mediaUrl={mediaUrl}
                                      isImage={mightBeImage}
                                      index={idx}
                                      allMediaUrls={message.media_urls || []}
                                      onOpenModal={(initialIndex) => {
                                        openImageViewer({
                                          images: mediaUrlsToViewerImages(message.media_urls || [], selectedBusiness?.id),
                                          index: initialIndex,
                                          editable: false,
                                        });
                                      }}
                                      displayInline={true}
                                    />
                                  );
                                })}
                              </div>
                            )}
                          </>
                        )}
                        
                        <div className={`text-xs font-medium mt-2 sm:mt-3 ${
                          isOutbound ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {messageTime}
                        </div>
                      </div>

                      {/* Call recording + transcript — separate bubbles below the call bubble */}
                      {message.message_type === 'call' && (
                        <CallRecordingDetails
                          recordingUrl={message.recording_url}
                          transcriptionText={message.transcription_text}
                          transcriptionStatus={message.transcription_status}
                          businessId={selectedBusiness?.id}
                          isOutbound={isOutbound}
                        />
                      )}

                      {isUnread && (
                        <button
                          onClick={() => {
                            // Email bubble = whole thread; everything else = that one item.
                            if (message.message_type === 'email') {
                              void markManyRead(unreadIdsIn(emailThreadFor(message)));
                            } else {
                              void markMessageAsRead(message.id);
                            }
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-all duration-200 active:scale-[0.98] cursor-pointer shadow-lg"
                          title="Mark as read"
                        >
                          Unread
                        </button>
                      )}
                    </div>
                    {isOutbound && (
                      <SenderBadge
                        businessId={selectedBusiness?.id}
                        sender={senderOf(message)}
                        direction="outbound"
                        aiResponse={message.ai_response}
                        isPhoneAgent={message.message_type === 'call' && !!message.agent_id}
                      />
                    )}
                  </div>
                );
              } else if (item.type === 'milestone' && item.milestone) {
                const milestone = item.milestone;
                const milestoneTime = new Date(milestone.created_at).toLocaleString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                });
                
                const getMilestoneIcon = (type: string, title?: string) => {
                  const iconClass = "h-5 w-5 text-slate-600";
                  
                  switch (type) {
                    case 'booking_scheduled':
                      return <Calendar className={iconClass} />;
                    case 'booking_completed':
                      return <CheckCircle className={iconClass} />;
                    case 'proposal_created':
                      return <FileText className={iconClass} />;
                    case 'custom':
                      if (title?.includes('Invoice Sent')) {
                        return <Mail className={iconClass} />;
                      } else if (title?.includes('Proposal Sent')) {
                        return <Mail className={iconClass} />;
                      } else if (title?.includes('Proposal Viewed')) {
                        return <Eye className={iconClass} />;
                      } else if (title?.includes('Proposal Approved')) {
                        return <CheckCircle className={iconClass} />;
                      }
                      return <CheckCircle className={iconClass} />;
                    default:
                      return <CheckCircle className={iconClass} />;
                  }
                };
                
                return (
                  <div key={item.id} className="flex justify-end mb-4 sm:mb-6">
                    <div 
                      className="max-w-[280px] sm:max-w-sm px-4 py-3 sm:px-5 sm:py-4 rounded-2xl border cursor-pointer transition-all duration-200 active:scale-[0.98] bg-slate-50 text-slate-700 border-slate-200 shadow-sm hover:shadow-md"
                      onClick={() => defaultHandleMilestoneClick(milestone)}
                      title={milestone.milestone.metadata?.entity_type === 'proposal' ? 'Click to view proposal details' : 'Click to view booking details'}
                    >
                      <div className="flex items-start space-x-3 sm:space-x-4">
                        <div className="flex-shrink-0 mt-0.5 sm:mt-1">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-100 rounded-xl sm:rounded-2xl flex items-center justify-center border border-slate-200">
                            {getMilestoneIcon(milestone.milestone.type, milestone.milestone.title)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm sm:text-base text-slate-800 mb-1.5 sm:mb-2">
                            {milestone.milestone.title}
                          </div>
                          {milestone.milestone.description && (
                            <div className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-2 sm:mb-3">
                              {milestone.milestone.description}
                            </div>
                          )}
                          <div className="text-xs sm:text-sm text-slate-500 font-semibold">
                            {milestoneTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              
              return null;
            })}
            
            {showScrollToBottom && (
              <button
                onClick={scrollToBottom}
                className="fixed bottom-32 right-8 bg-blue-500 hover:bg-blue-600 text-white p-4 rounded-2xl shadow-lg z-20 transition-all duration-200 active:scale-95 animate-bounce block"
                title="Scroll to bottom"
              >
                <ChevronDown className="h-6 w-6" />
              </button>
            )}
            
            {hasMoreTimeline && (
              <div className="flex justify-center py-4">
                <button
                  onClick={loadMoreTimeline}
                  disabled={loadingMoreTimeline}
                  className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 active:scale-95 flex items-center space-x-3 shadow-lg"
                >
                  {loadingMoreTimeline ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Show More Messages</span>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Message Input Area - iOS Style */}
      <div className="messages-input-area flex-shrink-0 px-3 py-3 sm:px-4 border-t border-gray-200 bg-white">
        <div>
          {/* Toolbar above the composer pill, matching the agent chat layout */}
          {(canSms || !hideInlineCreateButton) && (
          <div className="mb-2 flex items-center gap-2">
            {canSms && (
              <>
                {/* AI Reply Button for SMS */}
                <button
                  type="button"
                  onClick={async () => {
                    await handleAISMSReply();
                  }}
                  disabled={isGeneratingAI}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Generate AI SMS reply"
                >
                  {isGeneratingAI ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      <span>AI Reply</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setLocalSnippetsModalOpen(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98]"
                  title="Insert snippet"
                >
                  <Plus className="h-4 w-4" />
                  <span>Snippets</span>
                </button>

                {/* New Email — opens the full email composer (replaces the old SMS/Email toggle) */}
                {canEmail && (
                  <button
                    type="button"
                    onClick={() => setEmailComposeModalOpen(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-200 rounded-xl hover:bg-red-50 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98]"
                    title="Compose a new email"
                  >
                    <Mail className="h-4 w-4" />
                    <span>Email</span>
                  </button>
                )}
              </>
            )}
            {!hideInlineCreateButton && (
              <button
                type="button"
                onClick={() => setCreateModalOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98]"
                title="Create"
              >
                <Plus className="h-4 w-4" />
                <span>Create</span>
              </button>
            )}
          </div>
          )}

          {/* Main input — SMS composer by default; email-only contacts get a compose button */}
          <div className="relative">
            {canSms ? (
              <SMSReplyComposer
                businessId={selectedBusiness?.id || ''}
                value={messageInput}
                onChange={handleMessageInputChange}
                onSend={handleSendSMS}
                isSending={isSending}
                error={sendError}
                conversationId={card.id}
                customerId={card.customer_id}
                phoneNumber={card.type === 'phone' ? card.contact_info : undefined}
                shouldFetchAI={false}
                className="w-full"
              />
            ) : canEmail ? (
              <div className="w-full p-4">
                <button
                  type="button"
                  onClick={() => setEmailComposeModalOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Compose New Email
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Email Compose Modal – right-side mounted panel */}
      {emailComposeModalOpen && createPortal(
        <div
          className="fixed inset-0 transition-opacity duration-200 bg-black/50"
          style={{ zIndex: emailComposeZ, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          onClick={() => {
            setEmailComposeModalOpen(false);
            setIsReplyMode(false);
            setReplySubject('');
          }}
        >
          <div
            className="bg-white transform transition-all duration-200 fixed inset-y-0 right-0 w-full sm:w-[560px] shadow-2xl flex flex-col translate-x-0"
            style={{
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50/60 flex-shrink-0">
              <div className="text-sm font-semibold text-gray-800">{isReplyMode ? 'Reply' : 'New Message'}</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setLocalSnippetsModalOpen(true);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98]"
                  title="Insert snippet"
                >
                  <Plus className="h-4 w-4" />
                  <span>Snippets</span>
                </button>
                <button
                  onClick={() => {
                    setEmailComposeModalOpen(false);
                    setIsReplyMode(false);
                    setReplySubject('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="px-4 flex-shrink-0">
              <div className="flex items-center gap-2 border-b border-gray-200 py-2">
                <div className="text-sm text-gray-500 w-10">To</div>
                <input
                  className="flex-1 text-sm text-gray-800 bg-transparent outline-none"
                  value={emailAddress}
                  readOnly
                />
                <div className="text-xs text-gray-400">Cc Bcc</div>
              </div>
              {isReplyMode && replySubject && (
                <div className="flex items-center gap-2 border-b border-gray-200 py-2">
                  <div className="text-sm text-gray-500 w-10">Subject</div>
                  <input
                    className="flex-1 text-sm text-gray-800 bg-gray-50 outline-none cursor-not-allowed"
                    value={replySubject}
                    readOnly
                  />
                </div>
              )}
            </div>
            <div className="px-4 pb-4 flex-1 overflow-y-auto">
              <div className="relative">
                <EmailReplyComposer
                  businessId={selectedBusiness?.id || ''}
                  to={emailAddress}
                  defaultSubject={isReplyMode ? replySubject : ""}
                  placeholder="Write your message..."
                  onSendSuccess={(data, details) => {
                    handleEmailSendSuccess(data, details);
                    setEmailComposeModalOpen(false);
                    setIsReplyMode(false);
                    setReplySubject('');
                  }}
                  onSendError={handleEmailSendError}
                  disabled={isEmailDisabled}
                  className="w-full"
                  variant="gmail"
                  conversationId={card.id}
                  customerId={card.customer_id}
                  shouldFetchAI={false}
                  showSubjectInput={!isReplyMode}
                  subjectReadOnly={isReplyMode}
                  onInsertContent={(insertFn) => {
                    emailInsertRef.current = insertFn;
                  }}
                  onSubjectInsert={(insertFn) => {
                    emailSubjectInsertRef.current = insertFn;
                  }}
                  onAICompose={(currentBody, currentSubject) => {
                    setEmailBody(currentBody);
                    setCurrentEmailSubject(currentSubject);
                    setShowAIPrompt(true);
                  }}
                  isAIGenerating={isGeneratingAI}
                />
              </div>
            </div>
            
            {/* AI Prompt Input */}
            {showAIPrompt && (aiMode === 'compose' || aiMode === 'polish' || !emailBody.trim()) && (
              <div className="mx-4 mb-4 p-5 bg-gray-800 rounded-2xl shadow-2xl flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-gray-400">
                    <div className="flex flex-col gap-0.5">
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={aiPrompt}
                    onChange={(e) => setAIPrompt(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isGeneratingAI) {
                        if ((aiMode === 'compose' || !emailBody.trim()) && !aiPrompt.trim()) {
                          return;
                        }
                        e.preventDefault();
                        const mode = aiMode || 'compose';
                        handleGenerateAIEmail(mode);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setShowAIPrompt(false);
                        setAIPrompt('');
                        setAIMode(null);
                        setAIError(null);
                      }
                    }}
                    placeholder={aiMode === 'polish' ? 'Optional: e.g. make it longer, more formal...' : 'E.g. write a thank you email...'}
                    className="flex-1 bg-transparent text-white placeholder-gray-400 text-sm focus:outline-none py-1"
                    autoFocus
                    autoComplete="off"
                    disabled={isGeneratingAI}
                  />
                  {!isGeneratingAI ? (
                    <button
                      type="button"
                      onClick={() => {
                        const mode = aiMode || 'compose';
                        if ((mode === 'compose' || !emailBody.trim()) && !aiPrompt.trim()) {
                          return;
                        }
                        handleGenerateAIEmail(mode);
                      }}
                      disabled={(aiMode === 'compose' || !emailBody.trim()) && !aiPrompt.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      Generate
                    </button>
                  ) : (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  )}
                </div>
                {aiError && (
                  <div className="mt-3 px-3 py-2 bg-red-500/20 border border-red-400/30 text-red-200 text-sm rounded-lg">
                    {aiError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
      
      {/* AI Dropdown Menu - Shows when there's existing content */}
      {emailComposeModalOpen && showAIPrompt && emailBody.trim() && !aiMode && createPortal(
        <div 
          className="fixed inset-0 pointer-events-none flex items-center justify-center"
          style={{ zIndex: aiPromptZ }}
          onClick={() => {
            setShowAIPrompt(false);
          }}
        >
          <div className="pointer-events-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 py-2 min-w-[200px]">
              <button
                type="button"
                onClick={() => {
                  setAIMode('compose');
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 transition-colors"
              >
                <Sparkles className="h-4 w-4 text-blue-400" />
                <span>Compose</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAIPrompt(false);
                  handleGenerateAIEmail('autocomplete');
                }}
                disabled={isGeneratingAI}
                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                <svg className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <span>Auto Complete</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setAIMode('polish');
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 transition-colors"
              >
                <svg className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <span>Polish</span>
              </button>
              <div className="border-t border-gray-700 my-1"></div>
              <button
                type="button"
                onClick={() => {
                  setShowAIPrompt(false);
                  setAIPrompt('');
                  setAIMode(null);
                  setAIError(null);
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-400 hover:bg-gray-700 hover:text-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Snippets Modal */}
      {selectedBusiness && (
        <SnippetsModal
          isOpen={localSnippetsModalOpen}
          onClose={() => setLocalSnippetsModalOpen(false)}
          onSelectSnippet={handleSnippetSelect}
        />
      )}

      {/* Create Modal — not rendered when the parent provides its own create dropdown */}
      {!hideInlineCreateButton && (
        <CreateModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          customer={currentCustomer || null}
          onCreateAction={onCreateAction}
        />
      )}
    </div>
  );
}

